import { useMemo } from 'react';
import { Input, FormGroup, Label, Row, Col } from 'src/components/ui';
import JsonEditor from '../../JsonEditor';
import { InputLookupParams } from '../../customFields/hooks/useOptionsLookup';
import { LookupFieldObject } from '../../customFields/LookupFormField';
import { JnxTextExpr } from '../../../util/jnx';
import { t } from '@lingui/core/macro';
import styled from '@emotion/styled';
import useResourceLoader from '../../../util/useResourceLoader';
import ObjectSchemaApi from '../../../api/ObjectSchemaApi';
import Loader from '../../Loader';
import Notification from '../../Notification';

const PATTERN = { type: 'lookup-config' };

const OPTIONS = ['api', 'entity'] as const;
type OPTIONS_Type = typeof OPTIONS[number];

function LookupConfigurationField({
  fieldProps,
  value,
  readOnly,
  onChange,
}: {
  fieldProps: {
    label?: string;
    property?: string;
    forCount?: boolean;
  };
  readOnly?: boolean;
  value: string | LookupFieldObject;
  onChange: (value: LookupFieldObject) => void;
}) {
  const { label, property, forCount } = fieldProps || {};

  const lookupConfig = useMemo<LookupFieldObject>(() => {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (e) {
        return {};
      }
    }

    return value || {};
  }, [value]);

  function handleFieldChange(fieldName: string, fieldValue: any) {
    let newConfig: LookupFieldObject = (null as unknown) as LookupFieldObject;

    if (fieldName === 'lookupType') {
      const value = fieldValue as OPTIONS_Type;
      if (value === 'api') {
        newConfig = { ...lookupConfig, api: '' };
        delete newConfig.entity;
      } else {
        newConfig = { ...lookupConfig };
        delete newConfig.api;
      }
    } else {
      newConfig = {
        ...lookupConfig,
        [fieldName]: fieldValue,
      };
    }

    if (newConfig) onChange(newConfig);
  }

  const handleParamsChange = (newParams: InputLookupParams) => {
    const newConfig = {
      ...lookupConfig,
      params: newParams,
    };
    onChange(newConfig);
  };

  const lookupType: OPTIONS_Type = lookupConfig.api === undefined ? 'entity' : 'api';

  return (
    <FieldContainer>
      {label ? <Label className="title">{label}</Label> : null}
      <ConfigSection>
        <FormGroup className="full-row">
          <Label for={`${property}-resource`}>{t`Resource Name`}</Label>
          <Input
            type="text"
            id={`${property}-resource`}
            value={lookupConfig.resource || ''}
            readOnly={readOnly}
            placeholder="e.g., users, products"
            onChange={({ target: { value } }) => handleFieldChange('resource', value)}
          />
        </FormGroup>
        <FormGroup>
          <Label for={`${property}-api`}>{t`Lookup`}</Label>
          <Input
            type="select"
            className="form-control"
            id={`${property}-lookup-type`}
            value={lookupType}
            readOnly={readOnly}
            onChange={({ target: { value } }) => handleFieldChange('lookupType', value)}
          >
            {OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Input>
        </FormGroup>
        {lookupType === 'api' ? (
          <FormGroup>
            <Label for={`${property}-api`}>{t`API Endpoint`}</Label>
            <Input
              type="text"
              id={`${property}-api`}
              value={lookupConfig.api || ''}
              readOnly={readOnly}
              placeholder="e.g., /api/options"
              onChange={({ target: { value } }) => handleFieldChange('api', value)}
            />
          </FormGroup>
        ) : (
          <FormGroup>
            <Label for={`${property}-entity`}>{t`Entity Type`}</Label>
            <EntityTypeSelect
              id={`${property}-entity`}
              value={lookupConfig.entity}
              readOnly={readOnly}
              onChange={entityName => handleFieldChange('entity', entityName)}
            />
          </FormGroup>
        )}

        {forCount ? null : (
          <>
            <FormGroup>
              <Label for={`${property}-id`}>{t`ID Field`}</Label>
              <Input
                type="text"
                id={`${property}-id`}
                value={(lookupConfig.id as JnxTextExpr) || 'id'}
                readOnly={readOnly}
                placeholder="e.g., id"
                onChange={({ target: { value } }) => handleFieldChange('id', value as JnxTextExpr)}
              />
            </FormGroup>
            <FormGroup>
              <Label for={`${property}-label`}>{t`Label Field`}</Label>
              <Input
                type="text"
                id={`${property}-label`}
                value={(lookupConfig.label as JnxTextExpr) || 'name'}
                readOnly={readOnly}
                placeholder="e.g., name"
                onChange={({ target: { value } }) => handleFieldChange('label', value as JnxTextExpr)}
              />
            </FormGroup>
          </>
        )}

        <FormGroup>
          <Label for={`${property}-params`}>{t`Parameters (JSON)`}</Label>
          <JsonEditor value={lookupConfig.params || {}} readOnly={readOnly} onChange={handleParamsChange as any} maxHeight="9em" />
          <small className="form-text text-muted">{t`Enter parameters as JSON object. Example: {"key": "value"}`}</small>
        </FormGroup>
      </ConfigSection>
    </FieldContainer>
  );
}

function EntityTypeSelect({
  id,
  value,
  readOnly,
  onChange,
}: {
  id?: string;
  value: string | undefined | null;
  readOnly?: boolean;
  onChange: (entityName: string) => void;
}) {
  const [entities, loadingEntities, errorLoadingEntities] = useResourceLoader(ac => ObjectSchemaApi.list({ all: true }).then(x => x.items), []);

  return loadingEntities ? (
    <Loader>{t`Entity Types`}</Loader>
  ) : errorLoadingEntities ? (
    <Notification error={errorLoadingEntities} />
  ) : (
    <Input type="select" className="form-control" id={id} value={`${value}`} readOnly={readOnly} onChange={({ target: { value } }) => onChange(value)}>
      <option value="">---</option>
      {entities?.map(({ name }) => (
        <option key={name} value={name}>
          {name}
        </option>
      ))}
    </Input>
  );
}

const FieldContainer = styled('div')({});
const ConfigSection = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  '> .full-row': {
    gridColumnStart: 1,
    gridColumnEnd: 3,
  },
});

LookupConfigurationField.pattern = PATTERN;

export default LookupConfigurationField;
